import { Link } from "react-router-dom";
import { ArrowRight, Award } from "lucide-react";
import Reveal from "@/components/Reveal";

const team = [
    {
        name: "Aanya Reddy",
        role: "Founder & Principal Designer",
        image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=900&q=80",
        bio: "12 years between Mumbai, Milan and Hyderabad. Ex-Studio Lotus.",
    },
    {
        name: "Vikram Sastry",
        role: "Director of Architecture",
        image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=900&q=80",
        bio: "RIBA-trained, alumnus of Foster + Partners London.",
    },
    {
        name: "Priya Nair",
        role: "Head of Design Studio",
        image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=900&q=80",
        bio: "Ex-Livspace. NID Ahmedabad. Specialises in residential narrative.",
    },
    {
        name: "Karan Mehra",
        role: "Director of Execution",
        image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=900&q=80",
        bio: "18 years of project management across India and the GCC.",
    },
];

const awards = [
    { year: "2024", title: "Hyderabad Design Awards — Residence of the Year" },
    { year: "2024", title: "AD India 50 — Studios to Watch" },
    { year: "2023", title: "IIID Excellence — Best Modular Kitchen" },
    { year: "2023", title: "Elle Decor — Top 30 Indian Studios" },
    { year: "2022", title: "Architectural Digest — Featured Project of the Year" },
    { year: "2022", title: "Inside Outside — Commercial Interior of the Year" },
];

export default function About() {
    return (
        <div data-testid="about-page" className="bg-charcoal text-cream">
            {/* HEADER */}
            <section className="container-pad mx-auto pt-40 pb-20 md:pt-48 md:pb-32 border-b border-[#262626]">
                <Reveal>
                    <p className="overline mb-8">— About</p>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tighter text-cream max-w-5xl">
                        We design for the <em className="italic text-gold">long view.</em>
                    </h1>
                </Reveal>
            </section>

            {/* STORY */}
            <section className="container-pad mx-auto py-20 md:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="overline mb-6">— Our Story</p>
                            <h2 className="font-display text-4xl md:text-5xl text-cream leading-tight tracking-tight">
                                Founded in 2017<br />in Hyderabad.
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-[#D8D8D2] text-base md:text-lg leading-relaxed">
                        <Reveal delay={120}>
                            <p>
                                Midasis was founded by Aanya Reddy in 2017 with a simple thesis: that
                                Hyderabad — a city of poets, palaces and software — deserved an interior
                                practice equal to its scale of ambition. A practice that delivered the
                                quiet luxury of a Milan studio, with the operational rigour of a Bangalore
                                tech firm.
                            </p>
                            <p>
                                Eight years and 200+ projects later, we operate as a fully integrated
                                design-and-build studio out of Jubilee Hills. We are the practice of choice
                                for second-time homeowners, real estate developers building flagship
                                residences, and commercial brands launching their most considered spaces.
                            </p>
                            <p>
                                We believe in materials that age with grace, in design that doesn't shout,
                                in execution that respects the trade. Every Midasis project is — first and
                                last — a long-term relationship.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* VISION */}
            <section className="bg-charcoal-2 border-y border-[#262626] py-20 md:py-28">
                <div className="container-pad mx-auto">
                    <Reveal>
                        <p className="overline mb-8 text-center">— Founding Vision</p>
                        <p className="font-display italic text-2xl md:text-4xl lg:text-5xl text-cream leading-snug tracking-tight max-w-5xl mx-auto text-center">
                            &ldquo;We don't decorate rooms. We build the spatial backdrop to the most
                            important conversations of a family's life — and we treat that
                            responsibility like the privilege it is.&rdquo;
                        </p>
                        <p className="text-center mt-8 text-[#A3A3A3] text-sm uppercase tracking-[0.25em]">
                            — Aanya Reddy, Founder
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* TEAM */}
            <section className="container-pad mx-auto py-20 md:py-28">
                <div className="mb-16">
                    <Reveal>
                        <p className="overline mb-6">— Team</p>
                        <h2 className="font-display text-4xl md:text-6xl text-cream leading-tight tracking-tight max-w-3xl">
                            Specialists, not generalists.
                        </h2>
                    </Reveal>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {team.map((m, i) => (
                        <Reveal key={m.name} delay={i * 100}>
                            <article data-testid={`team-${i}`} className="group">
                                <div className="aspect-[4/5] overflow-hidden bg-charcoal-2 border border-[#1c1c1c]">
                                    <img
                                        src={m.image}
                                        alt={m.name}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                                    />
                                </div>
                                <div className="pt-6">
                                    <h3 className="font-display text-2xl text-cream tracking-tight">{m.name}</h3>
                                    <p className="overline mt-2">{m.role}</p>
                                    <p className="text-sm text-[#A3A3A3] mt-3 leading-relaxed">{m.bio}</p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* AWARDS */}
            <section className="bg-charcoal-2 border-t border-[#262626] py-20 md:py-28">
                <div className="container-pad mx-auto">
                    <Reveal>
                        <p className="overline mb-6">— Recognition</p>
                        <h2 className="font-display text-4xl md:text-6xl text-cream leading-tight tracking-tight max-w-3xl mb-16">
                            Awards & <em className="italic text-gold">accolades.</em>
                        </h2>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
                        {awards.map((a, i) => (
                            <Reveal key={a.title} delay={i * 60}>
                                <div data-testid={`award-${i}`} className="grid grid-cols-12 gap-6 py-6 border-t border-[#262626] items-center">
                                    <p className="col-span-2 font-display text-2xl md:text-3xl text-gold">{a.year}</p>
                                    <p className="col-span-9 text-cream text-base md:text-lg">{a.title}</p>
                                    <Award size={18} className="col-span-1 text-gold" />
                                </div>
                            </Reveal>
                        ))}
                        <div className="border-t border-[#262626] md:col-span-2" />
                    </div>
                </div>
            </section>

            <section className="cta-banner border-t border-[#262626] py-24 md:py-32">
                <div className="container-pad mx-auto text-center">
                    <Reveal>
                        <h2 className="font-display text-4xl md:text-6xl text-cream max-w-3xl mx-auto leading-tight tracking-tight">
                            Let's build something <em className="italic text-gold">timeless.</em>
                        </h2>
                        <Link to="/contact" className="btn-primary mt-10">
                            Talk to Us <ArrowRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
